import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import Toggle from 'dpl/common/design_system/Toggle';
import {
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { FILTER_SIMILAR_BREEDS } from 'dpl/constants/query_params';
import useSimilarBreedFilter from 'dpl/puppy_search_results/hooks/useSimilarBreedFilter';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';

import ExposedFilterBottomModal from './ExposedFilterBottomModal';
import FilterPill from './FilterPill';

const FILTER_TITLE = 'Similar breeds';

// The gate needs 5 breeders and says nothing about how many puppies they have
// between them, so a true count can undersell the filter. Below the floor the
// copy drops the number rather than printing "See +2 more puppies".
const PUPPY_COUNT_COPY_FLOOR = 5;

// The design asks for the toggle to be seen turning on before the modal goes,
// and `.DSToggle__slider` transitions over 0.3s - so the dismissal waits out
// the animation and a beat more.
const TOGGLE_ANIMATION_MS = 400;

export default function SimilarBreedsFilterButton({
  resultsType,
  totalItemCount
}) {
  const dispatch = useDispatch();
  const isSelected = useSelector(({ queryParams }) =>
    Boolean(queryParams[FILTER_SIMILAR_BREEDS])
  );
  const isMobileLayout = useSelector(({ server }) => server.isMobileLayout);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  useEffect(() => () => window.clearTimeout(closeTimeoutRef.current), []);

  // The toggle dismisses the modal on a delay, which would otherwise close it
  // again under a visitor who had closed and reopened it inside that window.
  const setFilterMenuOpen = useCallback(isOpen => {
    window.clearTimeout(closeTimeoutRef.current);
    setIsFilterMenuOpen(isOpen);
  }, []);

  const { isFilterVisible, puppyCount, radiusInMiles } =
    useSimilarBreedFilter();

  const analytics = {
    button_cta: FILTER_TITLE,
    view:
      resultsType === RESULTS_TYPES.PUPPIES
        ? ANALYTICS_VIEWS.PUPPY_SEARCH_RESULTS
        : ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS,
    view_feature: ANALYTICS_VIEW_FEATURES.SIMILAR_BREEDS_FILTER_BUTTON,
    view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_HEADER
  };

  // Removed outright rather than written as `false`: `Boolean('false')` is
  // true, so a lingering value would read as selected here.
  function handleToggleChange(event) {
    if (event.target.checked) {
      dispatch(pushParams({ [FILTER_SIMILAR_BREEDS]: true }, { hash: true }));
    } else {
      dispatch(resetParams(FILTER_SIMILAR_BREEDS, { hash: true }));
    }

    window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = window.setTimeout(
      () => setIsFilterMenuOpen(false),
      TOGGLE_ANIMATION_MS
    );
  }

  // `ExposedFilters` renders on desktop too. The server resolves the arm to
  // control off mobile, but the filter should not depend on that.
  if (!isMobileLayout || !isFilterVisible) {
    return null;
  }

  const puppyCountCopy =
    puppyCount >= PUPPY_COUNT_COPY_FLOOR
      ? `See +${puppyCount} more puppies within ${radiusInMiles} miles`
      : `See more puppies within ${radiusInMiles} miles`;

  return (
    <>
      <FilterPill
        onClick={() => setFilterMenuOpen(true)}
        label={FILTER_TITLE}
        isSelected={isSelected}
        analytics={analytics}
        isOpen={isFilterMenuOpen}
      />
      <ExposedFilterBottomModal
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setFilterMenuOpen}
        resultsType={resultsType}
        totalItemCount={totalItemCount}
        title={FILTER_TITLE}
        hideActions
      >
        <p className="font-16 lh-body secondary mb5">
          See more breeders and puppies available near you when you include
          similar breeds.
        </p>
        <div className="flex items-start gap-12 bt b--default pt5">
          <Toggle
            checked={isSelected}
            onChange={handleToggleChange}
            dataTestId="similar-breeds-toggle"
          />
          <div>
            <p className="font-16 fw-medium">Include similar breeds</p>
            <p className="font-14 secondary mt1">{puppyCountCopy}</p>
          </div>
        </div>
      </ExposedFilterBottomModal>
    </>
  );
}

SimilarBreedsFilterButton.propTypes = {
  resultsType: PropTypes.string.isRequired,
  totalItemCount: PropTypes.number.isRequired
};
